import {flow, onPatch, types} from 'mobx-state-tree';
import * as api from '../../services/api';
import WithFeedbackModel, {
  WithFeedbackActions
} from '../composables/withFeedbackModel';
import PaginationModel from '../PaginationModel';
import ReservationModel, {ReservationModelType} from '../ReservationModel';

export type ReservationsListActions = {
  loadEntries: (offset?: number, limit?: number) => void;
  loadNextPage: () => void;
  loadPreviousPage: () => void;
  // tslint:disable-next-line:no-any
  receiveEntries: (data: any) => void;
  filter: (str: string) => void;
  setFilterByTeamId: (id: number) => void;
};

export type ReservationsListViews = {
  entriesList: ReservationModelType[];
  pagedList: ReservationModelType[];
  totalCount: number;
};

const ReservationsList = types.compose(
  WithFeedbackModel,
  types
    .model('ReservationsList', {
      entries: types.map(ReservationModel),
      isLoading: types.optional(types.boolean, false),
      isLoaded: types.optional(types.boolean, false),
      pagination: types.optional(PaginationModel, {}),
      filterByTeamId: types.maybe(types.number),
      searchQuery: types.optional(types.string, '')
    })
    .views<ReservationsListViews>(self => {
      // tslint:disable-next-line:no-any
      const computed = (self as any) as ReservationsListViews;

      return {
        get entriesList() {
          return Array.from(self.entries.values());
        },
        get pagedList() {
          const {offset, nextOffset} = self.pagination;
          return computed.entriesList.slice(offset, nextOffset);
        },
        get totalCount() {
          return self.pagination.total;
        }
      };
    })
    .actions<ReservationsListActions>(self => {
      // tslint:disable-next-line:no-any
      const withActions = (self as any) as ReservationsListActions;

      // tslint:disable-next-line:no-any
      const withFeedback = (self as any) as WithFeedbackActions;

      return {
        loadEntries: flow(function*(_offset?: number) {
          self.isLoading = true;
          const response = yield api.getReservations({
            q: self.searchQuery,
            teamId: self.filterByTeamId,
            offset: _offset
          });

          if (response.status === 200 && response.data) {
            const {offset, limit, total} = response.data.metadata;
            self.pagination.offset = offset;
            self.pagination.limit = limit;
            self.pagination.total = total;
            self.pagination.addOffsetToCache(offset || 0);

            withActions.receiveEntries(response.data.reservations);
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isLoaded = true;
          self.isLoading = false;
        }),

        loadNextPage: function() {
          const {page, nextOffset} = self.pagination;
          page(nextOffset);
          withActions.loadEntries(nextOffset);
        },

        loadPreviousPage: function() {
          const {page, previousOffset} = self.pagination;
          page(previousOffset);
          withActions.loadEntries(previousOffset);
        },

        filter: function(searchQuery: string) {
          self.searchQuery = searchQuery;
        },

        setFilterByTeamId: function(id: number) {
          self.filterByTeamId = id;
        },

        // tslint:disable-next-line:no-any
        receiveEntries: function(entries: any) {
          if (!entries || entries.length === 0) {
            return;
          }

          const received = entries.reduce(
            (
              results: {[id: string]: ReservationModelType},
              r: ReservationModelType
            ) => ((results[String(r.id)] = r), results),
            {}
          );

          self.entries.merge(received);
        },

        afterCreate: function() {
          onPatch(self, patch => {
            if (
              patch.path === '/searchQuery' ||
              patch.path === '/filterByTeamId'
            ) {
              self.entries.clear();
              withActions.loadEntries();
            }
          });
        }
      };
    })
);

export type ReservationsListType = typeof ReservationsList.Type;

export default ReservationsList;



// WEBPACK FOOTER //
// ./src/models/ReservationsListModel/ReservationsListModel.ts